// src/components/AdmissionMedicationChart.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../styles/AdmissionMedicationChart.css';
import MedicationLog from './MedicationLog';
import MedicationOrder from './MedicationOrder';
import { useAuth } from '../../hooks/useAuth';

function MedicationChartForDate({ admissionId, planId, date, disableEdit }) {
  const [chartRows, setChartRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [nurseData, setNurseData] = useState({});
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (!planId || !date) return;
    setLoading(true);
    console.log('Fetching chart data for:', { admissionId, planId, date });
    
    axios
      .get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-chart/${admissionId}/plan-data`, {
        params: { planId, date },
      })
      .then((res) => {
        console.log('Chart data received:', res.data);
        setChartRows(res.data);
        // Set isActive based on the first row's item is_active property
        setIsActive(res.data[0]?.item?.is_active ?? false);
        console.log('Is active:', res.data[0]?.item?.is_active);
      })
      .catch((err) => {
        console.error('Error loading chart:', err.response?.data || err);
        setChartRows([]);
      })
      .finally(() => setLoading(false));
  }, [admissionId, planId, date]);

  // Fetch nurse information when chart data loads
  useEffect(() => {
    const fetchNurseInfo = async () => {
      const nurseIds = [...new Set(
        chartRows.flatMap(row => 
          row.logs
            .filter(log => log.nurse_id)
            .map(log => log.nurse_id)
        )
      )];

      if (nurseIds.length > 0) {
        try {
          const nursePromises = nurseIds.map(id =>
            axios.get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/employees/byID/${id}`)
          );
          const responses = await Promise.all(nursePromises);
          const nurses = {};
          responses.forEach((response, index) => {
            nurses[nurseIds[index]] = response.data.employee_name;
          });
          setNurseData(nurses);
        } catch (error) {
          console.error('Error fetching nurse data:', error);
        }
      }
    };

    if (chartRows.length > 0) {
      fetchNurseInfo();
    }
  }, [chartRows]);

  // Check if a time is in the future
  const isFutureTime = (scheduledTime, scheduledDate) => {
    const now = new Date();
    const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`);
     return scheduledDateTime > new Date(now.getTime() + 30 * 60 * 1000); // 30-minute buffer
  };

  // Check if administration was delayed by more than 2 hours
  const isSignificantlyDelayed = (scheduledTime, scheduledDate, administrationDateTime) => {
    if (!administrationDateTime) return false;
    
    const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`);
    const actualDateTime = new Date(administrationDateTime);
    const timeDifferenceHours = (actualDateTime - scheduledDateTime) / (1000 * 60 * 60);
    
    return timeDifferenceHours > 2;
  };

  // Check if any previous dose in the row was significantly delayed
  const hasDelayedPreviousDose = (logs, currentLogIndex) => {
    for (let i = 0; i < currentLogIndex; i++) {
      const log = logs[i];
      if (log.administration_datetime && isSignificantlyDelayed(log.scheduled_time, date, log.administration_datetime)) {
        return true;
      }
    }
    return false;
  };

  // Format header text for administered medications
  const formatHeaderText = (log) => {
    if (log.administration_datetime) {
      const actualTime = new Date(log.administration_datetime).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      const nurseName = nurseData[log.nurse_id] || 'Unknown';
      const isDelayed = isSignificantlyDelayed(log.scheduled_time, date, log.administration_datetime);
      
      return (
        <div>
          <span style={isDelayed ? {color: 'red'} : {}}>
            {log.scheduled_time} → {actualTime}
          </span>
          <div style={{ fontSize: '0.7em', fontWeight: 'normal', color: '#666' }}>
            ({nurseName})
          </div>
        </div>
      );
    }
    return log.scheduled_time;
  };

  const handleLogComplete = (updatedLog) => {
    setChartRows(rows => 
      rows.map(row => {
        // If this is the row containing the updated/new log
        if (row.item.order_id === updatedLog.order_id) {
          // For existing logs, update the matching log
          if (updatedLog.log_id && row.logs.some(log => log.log_id === updatedLog.log_id)) {
            return {
              ...row,
              logs: row.logs.map(log => 
                log.log_id === updatedLog.log_id ? updatedLog : log
              )
            };
          } else {
            // For new PRN entries, append the new log
            return {
              ...row,
              logs: [...row.logs, updatedLog].sort((a, b) => 
                a.scheduled_time.localeCompare(b.scheduled_time)
              )
            };
          }
        }
        return row;
      })
    );
    setSelectedLog(null);
  };

  const handleOrderUpdate = (updatedOrder) => {
    // Refresh the chart data
    axios
      .get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-chart/${admissionId}/plan-data`, {
        params: { planId, date },
      })
      .then((res) => {
        setChartRows(res.data);
      })
      .catch((err) => {
        console.error('Error refreshing chart:', err);
      });
  };

  const handleAddPRN = (item) => {
    const now = new Date();
    const scheduledTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    // For PRN medications without an existing log, create a new log entry
    const newLog = {
      order_id: item.order_id,
      scheduled_time: scheduledTime,
      scheduled_date: date,
      is_new_prn: true  // Flag to indicate this is a new PRN entry
    };
    
    setSelectedLog(newLog);
  };

  const handleLogClick = (log, item) => {
    // Check if it's a future time and prevent clicking
    if (isFutureTime(log.scheduled_time, date)) {
      return; // Don't open the modal for future times
    }

    // Check if medication is marked as not given and prevent clicking
    if (log.notGiven) {
      return; // Don't open the modal for not given medications
    }

    if (log.administration_datetime || disableEdit) return;
    
    const logData = {
      log_id: log.log_id,
      order_id: item.order_id,
      scheduled_time: log.scheduled_time,
      scheduled_date: date,
      is_new_prn: false,
      medication_name: item.medication_name
    };
    
    setSelectedLog(logData);
  };

  if (loading) return <p>Loading chart...</p>;
  if (!chartRows.length) return <p>No medications scheduled for this date.</p>;

  // Modified to consider both disableEdit and !isActive
  const isEditingDisabled = disableEdit || !isActive;

  return (
    <div className="med-chart">
      <h3>Plan #{planId} — {date}</h3>
      {chartRows.map(({ item, logs }) => (
        <div key={item.order_id} className="med-row">
          <div className="med-info">
            <div className="med-title">
              <strong>{item.medication_name}</strong> {item.dosage} ({item.route}) 
              {item.is_prn ? ' — PRN' : ` — ${item.frequency}`}
              {!isEditingDisabled && hasPermission('/admission-card/change-medication-schedule', 'Edit') && (
                <>
                  <button 
                    className="edit-schedule-btn"
                    onClick={() => setSelectedOrder(item)}
                    title="Edit schedule"
                  >
                    ✎
                  </button>
                  {item.is_prn && (
                    <button
                      className="add-prn-btn"
                      onClick={() => handleAddPRN(item)}
                      title="Add PRN administration"
                    >
                      + PRN
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <table className="mini-table">
            <thead>
              <tr>
                {logs.map((log, index) => {
                  const shouldHighlightRemaining = hasDelayedPreviousDose(logs, index) && !log.administration_datetime;
                  
                  return (
                    <th key={log.log_id} style={{ 
                      fontSize: '0.85em', 
                      padding: '4px',
                      color: shouldHighlightRemaining ? 'red' : 'inherit'
                    }}>
                      {formatHeaderText(log)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                {logs.map((log, index) => {
                  const isFuture = isFutureTime(log.scheduled_time, date);
                  const isNotGiven = log.notGiven;
                  const shouldHighlightRemaining = hasDelayedPreviousDose(logs, index) && !log.administration_datetime && !isNotGiven;
                  
                  return (
                    <td key={log.log_id}>
                      <button
                        className={`dose-slot${log.administration_datetime ? ' logged' : ''}${isFuture ? ' future-time' : ''}${isNotGiven ? ' not-given' : ''}`}
                        title={
                          isNotGiven
                            ? `Not given: ${log.reason}${log.reasonText ? ` - ${log.reasonText}` : ''}`
                            : isFuture
                            ? 'Cannot log medication for future time'
                            : log.administration_datetime
                            ? `Given by ${nurseData[log.nurse_id] || 'Unknown'} at ${new Date(log.administration_datetime).toLocaleTimeString()}`
                            : `Log ${item.medication_name} at ${log.scheduled_time}`
                        }
                        onClick={() => handleLogClick(log, item)}
                        disabled={log.administration_datetime || isEditingDisabled || isFuture || isNotGiven}
                        style={{
                          ...(isFuture ? { cursor: 'not-allowed', opacity: 0.5 } : {}),
                          ...(isNotGiven ? { cursor: 'not-allowed', opacity: 0.7 } : {}),
                          ...(shouldHighlightRemaining ? { borderColor: 'red', color: 'red' } : {})
                        }}
                      >
                        {log.administration_datetime ? '✔' : isNotGiven ? '✗' : isFuture ? '🕒' : ''}
                      </button>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      ))}
      {selectedLog && (
        <MedicationLog
          logId={selectedLog.log_id}
          orderId={selectedLog.order_id}
          scheduledTime={selectedLog.scheduled_time}
          scheduledDate={selectedLog.scheduled_date}
          isNewPRN={selectedLog.is_new_prn}
          onComplete={handleLogComplete}
          onCancel={() => setSelectedLog(null)}
        />
      )}
      {selectedOrder && (
        <MedicationOrder
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdate={handleOrderUpdate}
        />
      )}
    </div>
  );
}

const AdmissionMedicationChart = ({ admissionId, disableEdit }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('Fetching plans for admission:', admissionId);
    setLoading(true);
    
    axios
      .get(`${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-chart/plans/${admissionId}`)
      .then((res) => {
        console.log('Received plans:', res.data);
        setPlans(res.data);
        
        if (res.data.length) {
          const activePlan = res.data.find(p => p.isActive);
          const today = new Date().toISOString().slice(0, 10);

          if (activePlan && !disableEdit) {
            console.log('Found active plan:', activePlan);
            setSelectedPlan(activePlan.planId);
            setSelectedDate(today); // Always try to set today for active plan
          } else {
            // Fallback to first plan if no active plan
            setSelectedPlan(res.data[0].planId);
            setSelectedDate(res.data[0].dates[0]);
          }
        }
      })
      .catch((err) => console.error('Error fetching plans:', err))
      .finally(() => setLoading(false));
  }, [admissionId]);

  const today = new Date().toISOString().slice(0, 10);
  const hasMissing = (plan, date) => {
    // any log for that plan/date with administration_datetime == null?
    const foundPlan = plans.find((p) => p.planId === plan);
    if (!foundPlan) return false;
    // we don't know logs here, simplest: flag any past date
    return date < today;
  };

  if (loading) return <p>Loading plans...</p>;

  return (
    <div className="admission-med-chart-container">
      <nav className="plan-nav">
        {plans.map((plan) => (
          <div key={plan.planId}>
            <div
              className={`plan-item${plan.planId === selectedPlan ? ' selected' : ''}`}
              onClick={() => {
                setSelectedPlan(plan.planId);
                setSelectedDate(plan.dates[0]);
              }}
            >
              Plan #{plan.planId}
            </div>
            {plan.planId === selectedPlan && (
              <div className="date-list">
                {plan.dates.map((date) => (
                  <div
                    key={date}
                    className={`date-item${date === selectedDate ? ' selected' : ''}${hasMissing(plan.planId, date) && date !== today ? ' missing' : ''}`}
                    onClick={() => setSelectedDate(date)}
                  >
                    {date === today ? `${date} (Today)` : date}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      <main className="chart-panel">
        {selectedPlan && selectedDate ? (
          <MedicationChartForDate
            admissionId={admissionId}
            planId={selectedPlan}
            date={selectedDate}
            disableEdit={disableEdit}
          />
        ) : (
          <p>Select a plan and date to view the chart.</p>
        )}
      </main>
    </div>
  );
};

export default AdmissionMedicationChart;
